import Icon, {IconName} from '../icon/Icon';

export interface SocialNetwork {
    readonly name: string;
    readonly url: string;
}

/**
 * Les réseaux sociaux, lus **par leur nom** et non par leur position.
 *
 * ## Le défaut que cela retire
 *
 * Deux endroits écrivaient `socialNetworks[0]` pour Instagram et `[1]` pour Facebook, avec
 * l'icône codée en dur à côté. Trois choses cassaient alors en silence chez un autre client :
 * un seul réseau renseigné faisait planter la lecture de `[1]`, un ordre différent mettait
 * l'icône Instagram sur un lien Facebook, et un réseau supplémentaire n'apparaissait jamais.
 *
 * Ici la liste est parcourue, l'icône vient du nom, et un nom inconnu reçoit une icône de lien
 * générique plutôt que de disparaître : le libellé accessible porte de toute façon le nom du
 * réseau, donc rien n'est perdu pour qui ne voit pas l'icône.
 */
const ICONES: Readonly<Record<string, IconName>> = {
    instagram: 'instagram',
    facebook: 'facebook',
    tiktok: 'tiktok',
    x: 'twitter-x',
    twitter: 'twitter-x',
    linkedin: 'linkedin',
    youtube: 'youtube',
    pinterest: 'pinterest',
    whatsapp: 'whatsapp',
    snapchat: 'snapchat',
};

/**
 * Le nom typé de l'icône, et non plus une classe de police.
 *
 * `Record<string, IconName>` : un nom d'icône qui n'existe pas dans `Icon` ne compile plus. La
 * carte précédente désignait `bi-twitter-x`, absent de la version chargée — l'icône ne
 * s'affichait donc pas, en silence, et rien ne pouvait le signaler.
 */
export const iconeReseau = (nom: string): IconName =>
    ICONES[nom.trim().toLowerCase()] ?? 'link-45deg';

const SocialLinks = ({
    networks,
    className = '',
}: {
    readonly networks: readonly SocialNetwork[];
    readonly className?: string;
}) => {
    const visibles = networks.filter((reseau) => reseau.url.trim() !== '');
    if (visibles.length === 0) return null;

    return (
        <div className={`social-links ${className}`.trim()}>
            {visibles.map((reseau) => (
                <a
                    key={reseau.name}
                    className="social-link"
                    href={reseau.url}
                    aria-label={reseau.name}
                    rel="noreferrer"
                    target="_blank"
                >
                    <Icon name={iconeReseau(reseau.name)}/>
                </a>
            ))}
        </div>
    );
};

export default SocialLinks;
